import React, { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, getApiErrorMessage } from '../lib/api'
import { useToast } from '../ui/ToastProvider'
import { Button } from '../components/ui/button'
import { CLAIM_DOCUMENT_SECTIONS_ALL } from '../data/claimRequiredDocumentsGuide'
import MenuGuideButton from '../components/MenuGuideButton'
import { PageFrame, PageHeader } from '../ui/PageLayout'

const GENERATIONS = [
  { value: 'GEN1', label: '1세대', desc: '2009년 9월 이전 · 자기부담 거의 없음' },
  { value: 'GEN2', label: '2세대', desc: '2009년 10월~2017년 3월 · 자기부담 도입' },
  { value: 'GEN3', label: '3세대', desc: '2017년 4월~2021년 6월 · 급여/비급여 분리' },
  { value: 'GEN4', label: '4세대', desc: '2021년 7월~현재 · 비급여 특약 분리' },
]

const GENERATION_DETAILS = {
  GEN1: {
    badge: '보장 범위 가장 넓음',
    period: '2009년 9월 이전',
    title: '급여·비급여 구분 전 실손',
    points: [
      '급여와 비급여를 크게 나누지 않고 보상하는 구조입니다.',
      '자기부담금이 없거나 매우 낮은 계약이 많아 현재 판매 상품보다 유리한 경우가 많습니다.',
      '계약별 약관 차이가 커 실제 계산 전 증권 확인이 필요합니다.',
    ],
  },
  GEN2: {
    badge: '자기부담 도입',
    period: '2009년 10월 ~ 2017년 3월',
    title: '공제금액과 자기부담률이 본격 도입된 세대',
    points: [
      '입원은 급여·비급여 합산 후 90% 보상으로 단순 가정했습니다.',
      '통원은 병원 규모별 정액 공제와 20% 공제 중 큰 금액을 반영했습니다.',
      '실제 약제비 공제는 계약별로 다를 수 있어 약국 영수증 확인이 필요합니다.',
    ],
  },
  GEN3: {
    badge: '급여·비급여 분리',
    period: '2017년 4월 ~ 2021년 6월',
    title: '급여와 비급여를 분리해 각각 공제',
    points: [
      '급여는 정액 공제와 20% 공제 중 큰 금액을 차감합니다.',
      '비급여는 3만원과 30% 공제 중 큰 금액을 차감합니다.',
      '급여/비급여를 합산하지 않고 별도로 계산하는 것이 핵심입니다.',
    ],
  },
  GEN4: {
    badge: '현행 기준',
    period: '2021년 7월 이후',
    title: '비급여가 별도 특약으로 분리된 세대',
    points: [
      '급여 계산은 3세대와 유사한 구조로 단순화했습니다.',
      '비급여 특약 미가입이면 비급여 예상 보상액을 0원으로 처리합니다.',
      '비급여 특약 가입 여부와 연간 이용량에 따른 보험료 변동 가능성을 함께 확인해야 합니다.',
    ],
  },
}

const GENERATION_COMPARISON_ROWS = [
  { label: '입원 보상', gen1: '100% 가정', gen2: '90%', gen3: '급여 80% / 비급여 70%', gen4: '급여 80% / 비급여 특약' },
  { label: '통원 보상', gen1: '100% 가정', gen2: '80% 또는 공제 후', gen3: '급여/비급여 각각 공제', gen4: '급여/비급여 각각 공제' },
  { label: '비급여', gen1: '넓게 보장', gen2: '급여와 합산', gen3: '분리 보장', gen4: '특약 가입 시 보장' },
  { label: '통원 공제', gen1: '계약별 확인', gen2: '합산 1회', gen3: '각각 별도', gen4: '각각 별도' },
]

/** 이 화면의 예상 계산기와 맞춘 “단순화 가정” 요약 (약관·보험사별 실제와 다를 수 있음) */
const CALCULATOR_LOGIC_ROWS = [
  {
    label: '입원 진료비',
    gen1: '급·비 합산 전액을 보상으로 가정',
    gen2: '급·비 합산의 90%',
    gen3: '급여·비급여 각각 공제 후 보상(표준 구조)',
    gen4: '3세대와 유사, 비급여는 특약 필요',
  },
  {
    label: '통원 진료비',
    gen1: '합산 전액 가정',
    gen2: '병원 규모별 정액·20% 중 큰 공제(합산 1회)',
    gen3: '급여: 정액·20% 중 큰 공제 / 비급여: 3만·30% 중 큰 공제',
    gen4: '3세대와 동일, 비급여는 특약 미가입 시 0',
  },
  {
    label: '약제비',
    gen1: '합산 전액 가정',
    gen2: '8천원·20% 중 큰 공제(통원 시)',
    gen3: '급여: 8천·20% / 비급여: 3만·30%',
    gen4: '3세대와 동일, 비급여는 특약 반영',
  },
]

const HISTORY_CONSULT_CHECKS = [
  { title: '세대·계약일', items: ['증권의 최초 계약일·갱신 이력으로 세대 구분', '구실비·신실비 전환·특약 변경 여부'] },
  { title: '비급여·특약', items: ['4세대는 비급여 특약 가입·미가입에 따라 보장 공백 크게 달라짐', 'MRI 등 고액 비급여는 한도·특약 조건 확인'] },
  { title: '청구·서류', items: ['세부내역서·처방전으로 급여/비급여 구분', 'OCR·예상액은 참고용, 원본 대조 후 안내'] },
  { title: '유지·전환', items: ['1~2세대는 해지·대체 전 보장·보험료 비교', '감액·면책·연간 한도는 증권 약관 확인'] },
]

const HISTORY_PITFALLS = [
  '영수증 총액만 보고 급여 본인부담·비급여를 구분하지 않는 경우',
  '4세대에서 비급여 특약 없이 비급여를 실손으로 기대하는 경우',
  '통원·입원을 바꾸지 않고 계산해 공제 구조가 어긋나는 경우',
  '계산기 예상액을 확정 지급액처럼 안내하는 경우',
]

const HOSPITAL_TYPES = [
  { value: 'CLINIC', label: '의원/병원', deductible: 10000 },
  { value: 'GENERAL', label: '종합병원', deductible: 20000 },
  { value: 'TERTIARY', label: '상급종합병원', deductible: 20000 },
]

const DEFAULT_FORM = {
  generation: 'GEN3',
  visitType: 'OUTPATIENT',
  hospitalType: 'CLINIC',
  hasGen4NonBenefitRider: true,
  benefitPatientPaid: '',
  nonBenefitAmount: '',
  pharmacyBenefitPatientPaid: '',
  pharmacyNonBenefitAmount: '',
}

function onlyDigits(value) {
  return String(value || '').replace(/[^\d]/g, '')
}

function toNumber(value) {
  const n = Number(onlyDigits(value))
  return Number.isFinite(n) ? n : 0
}

function formatWon(value) {
  const n = Math.max(0, Math.round(Number(value) || 0))
  return `${n.toLocaleString('ko-KR')}원`
}

function amountAfterDeductible(amount, fixedDeductible, coinsuranceRate) {
  if (amount <= 0) return 0
  const deductible = Math.max(fixedDeductible, Math.round(amount * coinsuranceRate))
  return Math.max(0, amount - deductible)
}

function calculateEstimate(form) {
  const hospitalType = HOSPITAL_TYPES.find(item => item.value === form.hospitalType) || HOSPITAL_TYPES[0]
  const benefit = toNumber(form.benefitPatientPaid)
  const nonBenefit = toNumber(form.nonBenefitAmount)
  const pharmacyBenefit = toNumber(form.pharmacyBenefitPatientPaid)
  const pharmacyNonBenefit = toNumber(form.pharmacyNonBenefitAmount)
  const hospitalTotal = benefit + nonBenefit
  const pharmacyTotal = pharmacyBenefit + pharmacyNonBenefit
  const visitType = form.visitType
  const items = []
  let estimated = 0

  if (form.generation === 'GEN1') {
    estimated = hospitalTotal + pharmacyTotal
    items.push({ label: '1세대 예상 보상', amount: estimated, note: '입력 금액을 전액 보상으로 가정했습니다.' })
  } else if (form.generation === 'GEN2') {
    if (visitType === 'INPATIENT') {
      const hospitalPay = Math.round(hospitalTotal * 0.9)
      const pharmacyPay = Math.round(pharmacyTotal * 0.8)
      estimated = hospitalPay + pharmacyPay
      items.push({ label: '입원 진료비', amount: hospitalPay, note: '급여/비급여 합산 90% 보상 가정' })
      if (pharmacyTotal > 0) items.push({ label: '약제비', amount: pharmacyPay, note: '약제비 입력분 80% 보상 가정' })
    } else {
      const hospitalPay = amountAfterDeductible(hospitalTotal, hospitalType.deductible, 0.2)
      const pharmacyPay = pharmacyTotal > 0 ? amountAfterDeductible(pharmacyTotal, 8000, 0.2) : 0
      estimated = hospitalPay + pharmacyPay
      items.push({ label: '통원 진료비', amount: hospitalPay, note: `합산 금액에서 ${formatWon(hospitalType.deductible)} 또는 20% 중 큰 금액 공제` })
      if (pharmacyTotal > 0) items.push({ label: '약제비', amount: pharmacyPay, note: '약제비 별도 입력분 공제 후 계산' })
    }
  } else {
    const benefitPay = amountAfterDeductible(benefit, hospitalType.deductible, 0.2)
    const nonBenefitCovered = form.generation === 'GEN3' || form.hasGen4NonBenefitRider
    const nonBenefitPay = nonBenefitCovered ? amountAfterDeductible(nonBenefit, 30000, 0.3) : 0
    const pharmacyBenefitPay = pharmacyBenefit > 0 ? amountAfterDeductible(pharmacyBenefit, 8000, 0.2) : 0
    const pharmacyNonBenefitPay = nonBenefitCovered && pharmacyNonBenefit > 0
      ? amountAfterDeductible(pharmacyNonBenefit, 30000, 0.3)
      : 0
    estimated = benefitPay + nonBenefitPay + pharmacyBenefitPay + pharmacyNonBenefitPay
    items.push({ label: '급여 본인부담', amount: benefitPay, note: `${formatWon(hospitalType.deductible)} 또는 20% 중 큰 금액 공제` })
    items.push({
      label: '비급여',
      amount: nonBenefitPay,
      note: nonBenefitCovered ? '3만원 또는 30% 중 큰 금액 공제' : '4세대 비급여 특약 미가입으로 0원 처리',
    })
    if (pharmacyBenefit > 0) items.push({ label: '약제비 급여', amount: pharmacyBenefitPay, note: '8천원 또는 20% 중 큰 금액 공제' })
    if (pharmacyNonBenefit > 0) items.push({ label: '약제비 비급여', amount: pharmacyNonBenefitPay, note: nonBenefitCovered ? '3만원 또는 30% 중 큰 금액 공제' : '비급여 특약 미가입으로 0원 처리' })
  }

  return {
    estimated: Math.max(0, Math.round(estimated)),
    totalPaid: hospitalTotal + pharmacyTotal,
    selfPaid: Math.max(0, hospitalTotal + pharmacyTotal - estimated),
    items,
  }
}

function AmountInput({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-800">{label}</span>
      <input
        type="text"
        inputMode="numeric"
        value={value ? Number(value).toLocaleString('ko-KR') : ''}
        onChange={(e) => onChange(onlyDigits(e.target.value))}
        placeholder="예: 50,000"
        className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm tabular-nums outline-none focus:border-sgez-primary focus:ring-1 focus:ring-sgez-primary"
      />
    </label>
  )
}

function confidenceLabel(value) {
  const n = Number(value) || 0
  if (n >= 0.85) return '높음'
  if (n >= 0.65) return '보통'
  return '확인필요'
}

function ocrFieldRows(fields) {
  const labels = {
    benefitPatientPaid: '급여 본인부담금',
    nonBenefitAmount: '비급여 금액',
    totalPaid: '납부/수납 총액',
    pharmacyBenefitPatientPaid: '약제비 급여',
    pharmacyNonBenefitAmount: '약제비 비급여',
  }
  return Object.entries(labels).map(([key, label]) => ({
    key,
    label,
    value: fields?.[key]?.value,
    confidence: fields?.[key]?.confidence,
    sourceLine: fields?.[key]?.sourceLine,
  }))
}

const SUPPORT_TOOLS = [
  { id: 'medical', label: '실비 예상 계산기', desc: '실비 예상 환급금 계산' },
  { id: 'silson-history', label: '실비의료비 변천사', desc: '세대별 시기·공제·상담 체크' },
  { id: 'insurance-age', label: '보험나이·상령일', desc: '보험나이/상령일 계산' },
  { id: 'bmi', label: 'BMI 계산기', desc: '키/체중 기준 건강 상담 보조' },
  { id: 'waiting-period', label: '면책·감액기간', desc: '계약일 기준 종료일 계산' },
  { id: 'claim-docs', label: '청구 서류 체크', desc: '청구 유형별 필요 서류' },
  { id: 'hospital-search', label: '병원정보', desc: '의료기관 검색' },
  { id: 'non-payment', label: '비급여진료비', desc: '비급여 항목 조회' },
  { id: 'easy-drug', label: 'e약은요 검색', desc: '의약품 검색' },
  { id: 'risk-copy', label: '표현 리스크 검사', desc: '확정·과장 표현 사전 점검' },
]

const DISCLOSURE_GROUPS = [
  {
    title: '최근 3개월',
    items: [
      '의사에게 진찰·검사·치료·투약을 받은 적이 있나요?',
      '추가검사 또는 재검사를 권유받은 적이 있나요?',
      '건강검진에서 이상 소견 또는 추적 관찰 안내를 받은 적이 있나요?',
    ],
  },
  {
    title: '최근 1년',
    items: [
      '질병 확정 진단을 받거나 치료를 계속한 적이 있나요?',
      '수술·입원 예정 또는 정밀검사 예정이 있나요?',
      '동일 증상으로 반복 진료를 받은 적이 있나요?',
    ],
  },
  {
    title: '최근 5년',
    items: [
      '입원, 수술, 7일 이상 치료, 30일 이상 투약 이력이 있나요?',
      '암, 심장, 뇌혈관, 정신질환, 당뇨 등 주요 질환 진단 또는 치료 이력이 있나요?',
      '현재 복용 중인 약이나 정기 추적 관찰 중인 항목이 있나요?',
    ],
  },
]

const QUESTION_PURPOSES = [
  {
    id: 'first',
    label: '첫 상담',
    questions: [
      '이번 상담에서 가장 먼저 해결하고 싶은 걱정은 무엇인가요?',
      '현재 가입 중인 보험에서 잘 이해되지 않는 부분이 있나요?',
      '월 보험료는 어느 정도가 부담 없는 범위인가요?',
      '최근 병원 진료, 검사, 약 처방 이력이 있었나요?',
    ],
  },
  {
    id: 'coverage',
    label: '보장 점검',
    questions: [
      '현재 가장 걱정되는 보장 공백은 진단비, 수술비, 실손, 운전자 중 어디인가요?',
      '기존 보험에서 갱신형 담보나 보험료 인상 부담을 느낀 적이 있나요?',
      '가족력이나 직업상 특별히 신경 쓰는 위험이 있나요?',
      '기존 계약을 유지하면서 보완하고 싶은지, 전체 재정리를 원하는지 확인해도 될까요?',
    ],
  },
  {
    id: 'claim',
    label: '청구 상담',
    questions: [
      '진료일, 병원명, 진단명 또는 주요 증상은 무엇인가요?',
      '입원, 수술, 통원, 약 처방 중 어떤 내용이 있었나요?',
      '영수증, 세부내역서, 처방전 등 준비된 서류가 있나요?',
      '기존에 같은 사고나 질병으로 청구한 이력이 있나요?',
    ],
  },
]

function ClaimDocumentsGuide({ section }) {
  if (!section?.panels?.length) return null
  return (
    <div className="mt-4 border-t border-slate-200 pt-4">
      <div className="mb-1 flex items-center gap-2">
        <span className="h-4 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
        <div className="text-sm font-semibold text-slate-800">필요 서류</div>
      </div>
      <p className="mb-3 text-xs text-slate-500">
        {section.sectionTitle === '일반'
          ? '대부분의 청구에서 함께 확인되는 서류입니다. 보험사·담보별로 추가 서류가 있을 수 있습니다.'
          : '해당 청구 유형에서 통상 요구되는 서류입니다. 보험사·약관에 따라 다를 수 있습니다.'}
      </p>
      <div className="space-y-2">
        {section.panels.map((panel) => (
          <details key={`${section.sectionTitle}-${panel.title}`} open={panel.defaultOpen} className="group rounded-lg border border-slate-200 bg-white">
            <summary className="cursor-pointer list-none px-3 py-2.5 text-sm font-bold text-slate-800 marker:hidden [&::-webkit-details-marker]:hidden">
              <span className="flex items-center justify-between gap-2">
                <span>{panel.title}</span>
                <span className="text-xs font-normal text-slate-400 group-open:hidden">펼치기</span>
                <span className="hidden text-xs font-normal text-slate-400 group-open:inline">접기</span>
              </span>
            </summary>
            <div className="space-y-2 border-t border-slate-100 px-3 py-2.5 text-sm leading-relaxed text-slate-700">
              {panel.blocks.map((block, bi) => (
                <div key={bi}>
                  {block.strong && <div className="font-semibold text-slate-900">{block.strong}</div>}
                  {block.lines?.filter(Boolean).map((line) => <div key={line} className="mt-1 text-xs leading-5 text-slate-600">{line}</div>)}
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>
    </div>
  )
}

function buildMemoSummary(raw) {
  const text = String(raw || '').trim()
  if (!text) return null
  const sentences = text.split(/[\n.。!?]+/).map((s) => s.trim()).filter(Boolean)
  const summary = sentences.slice(0, 2).join(' / ') || text.slice(0, 80)
  const needKeywords = ['원함', '희망', '걱정', '부담', '필요', '관심']
  const actionKeywords = ['다음', '확인', '전달', '발송', '예약', '전화', '연락']
  const needs = sentences.filter((s) => needKeywords.some((k) => s.includes(k))).slice(0, 3)
  const actions = sentences.filter((s) => actionKeywords.some((k) => s.includes(k))).slice(0, 3)
  return [
    `상담 요약: ${summary}`,
    `고객 니즈: ${needs.length ? needs.join(' / ') : '추가 확인 필요'}`,
    `확인 필요: 고지사항, 기존 계약, 보험료 부담 가능 범위`,
    `다음 액션: ${actions.length ? actions.join(' / ') : '후속 연락 일정과 필요 서류 확인'}`,
    `후속 메시지 초안: 오늘 상담 내용 기준으로 필요한 자료를 정리해 다시 안내드리겠습니다.`,
  ]
}

function unwrapApiData(res) {
  const body = res?.data
  return body?.data !== undefined ? body.data : body
}

function firstField(row, keys) {
  if (!row || !keys?.length) return ''
  for (const key of keys) {
    const value = row[key]
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      return String(value).trim()
    }
  }
  return ''
}

function formatPublicDataCount(value) {
  if (value === null || value === undefined || value === '') return '-'
  const n = Number(value)
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : String(value)
}

function PublicDataError({ message }) {
  if (!message) return null
  return (
    <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
      {message}
    </div>
  )
}

function SimpleSupportTool({ activeTool }) {
  const [text, setText] = useState('')
  const [birth, setBirth] = useState('')
  const [height, setHeight] = useState('')
  const [weight, setWeight] = useState('')
  const [contractDate, setContractDate] = useState('')
  const [disclosureChecked, setDisclosureChecked] = useState({})
  const [questionPurpose, setQuestionPurpose] = useState('first')
  const [claimDocSectionTitle, setClaimDocSectionTitle] = useState('일반')
  const [hospitalSearch, setHospitalSearch] = useState({ q: '', sido: '' })
  const [hospitalResult, setHospitalResult] = useState(null)
  const [hospitalLoading, setHospitalLoading] = useState(false)
  const [hospitalError, setHospitalError] = useState('')
  const [nonPaymentSearch, setNonPaymentSearch] = useState({ hospitalName: '', itemCd: '' })
  const [nonPaymentResult, setNonPaymentResult] = useState(null)
  const [nonPaymentLoading, setNonPaymentLoading] = useState(false)
  const [nonPaymentError, setNonPaymentError] = useState('')
  const [drugSearch, setDrugSearch] = useState({ q: '', entpName: '' })
  const [drugResult, setDrugResult] = useState(null)
  const [drugLoading, setDrugLoading] = useState(false)
  const [drugError, setDrugError] = useState('')
  const selected = SUPPORT_TOOLS.find((t) => t.id === activeTool) || SUPPORT_TOOLS[0]

  const birthResult = useMemo(() => {
    if (!birth) return null
    const d = new Date(`${birth}T00:00:00`)
    if (Number.isNaN(d.getTime())) return null
    const today = new Date()
    let age = today.getFullYear() - d.getFullYear()
    const passed = today.getMonth() > d.getMonth() || (today.getMonth() === d.getMonth() && today.getDate() >= d.getDate())
    if (!passed) age -= 1
    const maturity = new Date(d)
    maturity.setMonth(maturity.getMonth() + 6)
    return { age, maturity: `${maturity.getFullYear()}-${String(maturity.getMonth() + 1).padStart(2, '0')}-${String(maturity.getDate()).padStart(2, '0')}` }
  }, [birth])

  const bmi = useMemo(() => {
    const h = Number(height) / 100
    const w = Number(weight)
    if (!h || !w) return null
    const value = w / (h * h)
    const label = value < 18.5 ? '저체중' : value < 23 ? '정상' : value < 25 ? '과체중' : '비만'
    return { value: value.toFixed(1), label }
  }, [height, weight])

  const periodResult = useMemo(() => {
    if (!contractDate) return null
    const d = new Date(`${contractDate}T00:00:00`)
    if (Number.isNaN(d.getTime())) return null
    const addDaysLocal = (days) => {
      const x = new Date(d)
      x.setDate(x.getDate() + days)
      return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`
    }
    return { days90: addDaysLocal(90), year1: addDaysLocal(365), years2: addDaysLocal(730) }
  }, [contractDate])

  const copyLines = async (lines) => {
    const value = (Array.isArray(lines) ? lines.join('\n') : String(lines || '')).trim()
    if (!value) return
    try {
      await navigator.clipboard.writeText(value)
    } catch (e) {
      // 브라우저 권한이 막힌 경우에도 화면 출력은 그대로 제공한다.
    }
  }

  const searchHospitals = async () => {
    const q = String(hospitalSearch.q || '').trim()
    const sido = String(hospitalSearch.sido || '').trim()
    if (!q && !sido) {
      setHospitalError('기관명 또는 지역명을 입력해 주세요.')
      return
    }
    setHospitalLoading(true)
    setHospitalError('')
    try {
      const data = unwrapApiData(await api.get('/health/hira-reference-data/medical-institutions', {
        params: { q: q || undefined, sido: sido || undefined, size: 30 },
      }))
      setHospitalResult(data || null)
    } catch (e) {
      setHospitalResult(null)
      setHospitalError(getApiErrorMessage(e, '병원정보를 불러오지 못했습니다.'))
    } finally {
      setHospitalLoading(false)
    }
  }

  const searchNonPayments = async () => {
    const yadmNm = String(nonPaymentSearch.hospitalName || '').trim()
    const itemCd = String(nonPaymentSearch.itemCd || '').trim()
    if (!yadmNm && !itemCd) {
      setNonPaymentError('병원명 또는 비급여 항목코드를 입력해 주세요.')
      return
    }
    setNonPaymentLoading(true)
    setNonPaymentError('')
    try {
      const data = unwrapApiData(await api.get('/health/hira-reference-data/non-payment/hospital-details', {
        params: { yadmNm: yadmNm || undefined, itemCd: itemCd || undefined, size: 30 },
      }))
      setNonPaymentResult(data || null)
    } catch (e) {
      setNonPaymentResult(null)
      setNonPaymentError(getApiErrorMessage(e, '비급여진료비 정보를 불러오지 못했습니다.'))
    } finally {
      setNonPaymentLoading(false)
    }
  }

  const searchEasyDrugs = async () => {
    const q = String(drugSearch.q || '').trim()
    const entpName = String(drugSearch.entpName || '').trim()
    if (!q && !entpName) {
      setDrugError('의약품명 또는 업체명을 입력해 주세요.')
      return
    }
    setDrugLoading(true)
    setDrugError('')
    try {
      const data = unwrapApiData(await api.get('/health/drug-reference/easy-drugs', {
        params: { q: q || undefined, entpName: entpName || undefined, size: 30 },
      }))
      setDrugResult(data || null)
    } catch (e) {
      setDrugResult(null)
      setDrugError(getApiErrorMessage(e, '의약품 정보를 불러오지 못했습니다.'))
    } finally {
      setDrugLoading(false)
    }
  }

  const disclosureMemo = useMemo(() => {
    const selectedLines = DISCLOSURE_GROUPS.flatMap((group) => (
      group.items
        .filter((item) => disclosureChecked[`${group.title}:${item}`])
        .map((item) => `- ${group.title}: ${item}`)
    ))
    if (!selectedLines.length) {
      return [
        '체크된 고지 확인 항목이 없습니다.',
        '상담 전 최근 3개월, 1년, 5년 기준으로 병원·검사·투약·입원·수술 이력을 확인해 주세요.',
      ]
    }
    return [
      '[고지사항 상담 확인]',
      ...selectedLines,
      '확인 필요: 해당 항목의 진료일, 병원명, 치료/검사 내용, 투약 기간을 고객에게 추가 확인',
    ]
  }, [disclosureChecked])

  const selectedClaimDocSection = useMemo(
    () => CLAIM_DOCUMENT_SECTIONS_ALL.find((s) => s.sectionTitle === claimDocSectionTitle) || CLAIM_DOCUMENT_SECTIONS_ALL[0],
    [claimDocSectionTitle]
  )
  const selectedQuestionPurpose = QUESTION_PURPOSES.find((item) => item.id === questionPurpose) || QUESTION_PURPOSES[0]
  const memoSummary = useMemo(() => buildMemoSummary(text), [text])

  if (activeTool === 'insurance-age') {
    return (
      <ToolCard title={selected.label} desc={selected.desc}>
        <label className="block text-xs font-medium text-slate-600">생년월일 (캘린더에서 선택)</label>
        <input type="date" className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm" value={birth} onChange={(e) => setBirth(e.target.value)} />
        {birthResult && <ResultBox lines={[`만 나이: ${birthResult.age}세`, `보험 상령일 기준일: ${birthResult.maturity}`]} />}
      </ToolCard>
    )
  }
  if (activeTool === 'bmi') {
    return (
      <ToolCard title={selected.label} desc={selected.desc}>
        <div className="grid gap-2 sm:grid-cols-2">
          <input type="number" className="rounded border border-slate-300 px-3 py-2 text-sm" placeholder="키 cm" value={height} onChange={(e) => setHeight(e.target.value)} />
          <input type="number" className="rounded border border-slate-300 px-3 py-2 text-sm" placeholder="체중 kg" value={weight} onChange={(e) => setWeight(e.target.value)} />
        </div>
        {bmi && <ResultBox lines={[`BMI: ${bmi.value}`, `판정: ${bmi.label}`]} />}
      </ToolCard>
    )
  }
  if (activeTool === 'waiting-period') {
    return (
      <ToolCard title={selected.label} desc={selected.desc}>
        <label className="block text-xs font-medium text-slate-600">계약일 (캘린더에서 선택)</label>
        <input type="date" className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm" value={contractDate} onChange={(e) => setContractDate(e.target.value)} />
        {periodResult && <ResultBox lines={[`90일 기준: ${periodResult.days90}`, `1년 기준: ${periodResult.year1}`, `2년 기준: ${periodResult.years2}`]} />}
      </ToolCard>
    )
  }
  if (activeTool === 'claim-docs') {
    return (
      <ToolCard title={selected.label} desc="청구 유형을 선택하면 해당 유형에 필요한 서류만 표시됩니다.">
        <div>
          <label htmlFor="claim-doc-section" className="mb-1.5 block text-xs font-medium text-slate-700">청구 유형</label>
          <select
            id="claim-doc-section"
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
            value={claimDocSectionTitle}
            onChange={(e) => setClaimDocSectionTitle(e.target.value)}
          >
            {CLAIM_DOCUMENT_SECTIONS_ALL.map((s) => (
              <option key={s.sectionTitle} value={s.sectionTitle}>{s.sectionTitle}</option>
            ))}
          </select>
        </div>
        <ClaimDocumentsGuide section={selectedClaimDocSection} />
      </ToolCard>
    )
  }
  if (activeTool === 'hospital-search') {
    const items = Array.isArray(hospitalResult?.items) ? hospitalResult.items : []
    return (
      <ToolCard title={selected.label} desc="병원명과 지역으로 의료기관 정보를 검색합니다.">
        <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_180px_auto]">
          <input
            type="search"
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            placeholder="병원명"
            value={hospitalSearch.q}
            onChange={(e) => setHospitalSearch((prev) => ({ ...prev, q: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') searchHospitals()
            }}
          />
          <input
            type="search"
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            placeholder="지역"
            value={hospitalSearch.sido}
            onChange={(e) => setHospitalSearch((prev) => ({ ...prev, sido: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') searchHospitals()
            }}
          />
          <Button type="button" variant="sgez" size="sm" onClick={searchHospitals} disabled={hospitalLoading}>
            {hospitalLoading ? '검색 중...' : '검색'}
          </Button>
        </div>
        <PublicDataError message={hospitalError} />
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="w-full min-w-[640px] border-collapse text-left text-xs">
            <thead className="bg-sgez-surface-sunken text-slate-500">
              <tr>
                <th className="px-3 py-2">기관명</th>
                <th className="px-3 py-2">종별</th>
                <th className="px-3 py-2">지역</th>
                <th className="px-3 py-2">주소</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length > 0 ? items.map((row, idx) => (
                <tr key={`${row.name || idx}-${row.address || idx}`} className="align-top">
                  <td className="px-3 py-2 font-semibold text-slate-900">{row.name || '-'}</td>
                  <td className="px-3 py-2 text-slate-700">{row.institutionType || '-'}</td>
                  <td className="px-3 py-2 text-slate-700">{row.sidoName || '-'}</td>
                  <td className="px-3 py-2 text-slate-600">{row.address || '-'}</td>
                </tr>
              )) : (
                <tr>
                  <td className="px-3 py-6 text-center text-slate-500" colSpan={4}>검색 결과 없음</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </ToolCard>
    )
  }
  if (activeTool === 'non-payment') {
    const items = Array.isArray(nonPaymentResult?.items) ? nonPaymentResult.items : []
    return (
      <ToolCard title={selected.label} desc="병원명 또는 비급여 항목코드로 금액 정보를 조회합니다.">
        <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_220px_auto]">
          <input
            type="search"
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            placeholder="병원명"
            value={nonPaymentSearch.hospitalName}
            onChange={(e) => setNonPaymentSearch((prev) => ({ ...prev, hospitalName: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') searchNonPayments()
            }}
          />
          <input
            type="search"
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            placeholder="비급여 항목코드"
            value={nonPaymentSearch.itemCd}
            onChange={(e) => setNonPaymentSearch((prev) => ({ ...prev, itemCd: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') searchNonPayments()
            }}
          />
          <Button type="button" variant="sgez" size="sm" onClick={searchNonPayments} disabled={nonPaymentLoading}>
            {nonPaymentLoading ? '검색 중...' : '검색'}
          </Button>
        </div>
        <PublicDataError message={nonPaymentError} />
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="w-full min-w-[760px] border-collapse text-left text-xs">
            <thead className="bg-sgez-surface-sunken text-slate-500">
              <tr>
                <th className="px-3 py-2">병원</th>
                <th className="px-3 py-2">항목</th>
                <th className="px-3 py-2">금액</th>
                <th className="px-3 py-2">지역</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length > 0 ? items.map((row, idx) => {
                const hospital = firstField(row, ['yadmNm', 'yadmName', 'hospitalName'])
                const itemName = firstField(row, ['npayKorNm', 'itemNm', 'itemName', 'trtItemNm'])
                const amount = firstField(row, ['curAmt', 'cost', 'amount', 'minPrc', 'maxPrc', 'minAmt'])
                const region = [firstField(row, ['sidoCdNm', 'sidoNm']), firstField(row, ['sgguCdNm', 'sgguNm'])].filter(Boolean).join(' ')
                return (
                  <tr key={`${hospital || idx}-${itemName || idx}`} className="align-top">
                    <td className="px-3 py-2 font-semibold text-slate-900">{hospital || '-'}</td>
                    <td className="px-3 py-2 text-slate-700">{itemName || firstField(row, ['itemCd']) || '-'}</td>
                    <td className="px-3 py-2 tabular-nums text-slate-700">{formatPublicDataCount(amount)}</td>
                    <td className="px-3 py-2 text-slate-600">{region || firstField(row, ['addr']) || '-'}</td>
                  </tr>
                )
              }) : (
                <tr>
                  <td className="px-3 py-6 text-center text-slate-500" colSpan={4}>검색 결과 없음</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </ToolCard>
    )
  }
  if (activeTool === 'easy-drug') {
    const items = Array.isArray(drugResult?.items) ? drugResult.items : []
    return (
      <ToolCard title={selected.label} desc="의약품명, 업체명, 품목기준코드 기준으로 검색합니다.">
        <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_220px_auto]">
          <input
            type="search"
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            placeholder="의약품명"
            value={drugSearch.q}
            onChange={(e) => setDrugSearch((prev) => ({ ...prev, q: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') searchEasyDrugs()
            }}
          />
          <input
            type="search"
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            placeholder="업체명"
            value={drugSearch.entpName}
            onChange={(e) => setDrugSearch((prev) => ({ ...prev, entpName: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') searchEasyDrugs()
            }}
          />
          <Button type="button" variant="sgez" size="sm" onClick={searchEasyDrugs} disabled={drugLoading}>
            {drugLoading ? '검색 중...' : '검색'}
          </Button>
        </div>
        <PublicDataError message={drugError} />
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="w-full min-w-[720px] border-collapse text-left text-xs">
            <thead className="bg-sgez-surface-sunken text-slate-500">
              <tr>
                <th className="px-3 py-2">의약품명</th>
                <th className="px-3 py-2">업체</th>
                <th className="px-3 py-2">품목기준코드</th>
                <th className="px-3 py-2">검색 참고</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length > 0 ? items.map((row, idx) => {
                const name = firstField(row, ['itemName', 'ITEM_NAME'])
                const company = firstField(row, ['entpName', 'ENTP_NAME'])
                const itemSeq = firstField(row, ['itemSeq', 'ITEM_SEQ'])
                const effect = firstField(row, ['efcyQesitm', 'EFCY_QESITM'])
                return (
                  <tr key={`${itemSeq || name || idx}`} className="align-top">
                    <td className="px-3 py-2 font-semibold text-slate-900">{name || '-'}</td>
                    <td className="px-3 py-2 text-slate-700">{company || '-'}</td>
                    <td className="px-3 py-2 text-slate-700">{itemSeq || '-'}</td>
                    <td className="px-3 py-2 text-slate-600">{effect ? `${effect.slice(0, 80)}${effect.length > 80 ? '...' : ''}` : '-'}</td>
                  </tr>
                )
              }) : (
                <tr>
                  <td className="px-3 py-6 text-center text-slate-500" colSpan={4}>검색 결과 없음</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </ToolCard>
    )
  }
  const presets = {
    disclosure: ['최근 3개월 내 진료·검사·투약 여부', '최근 1년 내 추가검사/재검사 소견 여부', '최근 5년 내 입원·수술·7일 이상 치료 여부', '현재 복용 중인 약 여부'],
    'question-guide': ['오늘 상담의 목적은 무엇인가요?', '가장 걱정되는 보장 공백은 무엇인가요?', '최근 병원 진료나 청구 경험이 있나요?', '보험료 부담에서 원하는 수준이 있나요?'],
    'memo-summary': ['상담 요약', '고객 니즈', '확인 필요 사항', '다음 액션', '후속 메시지 초안'],
  }
  if (activeTool === 'risk-copy') {
    const risky = ['무조건', '100%', '최고', '가장', '확정', '누구나', '반드시'].filter((word) => text.includes(word))
    return (
      <ToolCard title={selected.label} desc={selected.desc}>
        <textarea className="min-h-[160px] w-full rounded border border-slate-300 px-3 py-2 text-sm" value={text} onChange={(e) => setText(e.target.value)} placeholder="검사할 문구를 붙여넣으세요." />
        <ResultBox lines={risky.length ? risky.map((w) => `주의 표현: "${w}"`) : ['감지된 대표 위험 표현이 없습니다.', '발송 전 약관·조건·심사 결과에 따라 달라질 수 있다는 문구를 확인해 주세요.']} />
      </ToolCard>
    )
  }
  if (activeTool === 'disclosure') {
    return (
      <ToolCard title={selected.label} desc="기간별 고지 질문을 상담 전에 체크하고, 확인이 필요한 항목을 상담 메모로 정리합니다.">
        <div className="grid gap-3 lg:grid-cols-3">
          {DISCLOSURE_GROUPS.map((group) => (
            <div key={group.title} className="rounded-xl border border-slate-200 bg-slate-50/80 p-3">
              <div className="text-sm font-bold text-slate-900">{group.title}</div>
              <div className="mt-2 space-y-2">
                {group.items.map((item) => {
                  const key = `${group.title}:${item}`
                  return (
                    <label key={key} className="flex gap-2 rounded-lg bg-white px-2 py-2 text-sm leading-5 text-slate-700">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={!!disclosureChecked[key]}
                        onChange={(e) => setDisclosureChecked((prev) => ({ ...prev, [key]: e.target.checked }))}
                      />
                      <span>{item}</span>
                    </label>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
        <ResultBox lines={disclosureMemo} />
        <div className="flex justify-end">
          <Button type="button" variant="sgezOutline" size="sm" onClick={() => copyLines(disclosureMemo)}>상담 메모로 복사</Button>
        </div>
      </ToolCard>
    )
  }
  if (activeTool === 'question-guide') {
    return (
      <ToolCard title={selected.label} desc="상담 목적별로 처음 물어볼 질문을 추천합니다. 고객상세에서 바로 진입해 상담 준비용으로 사용할 수 있습니다.">
        <select className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={questionPurpose} onChange={(e) => setQuestionPurpose(e.target.value)}>
          {QUESTION_PURPOSES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
        </select>
        <ResultBox lines={selectedQuestionPurpose.questions.map((q, idx) => `${idx + 1}. ${q}`)} />
        <div className="flex justify-end">
          <Button type="button" variant="sgezOutline" size="sm" onClick={() => copyLines(selectedQuestionPurpose.questions.map((q, idx) => `${idx + 1}. ${q}`))}>질문 복사</Button>
        </div>
      </ToolCard>
    )
  }
  if (activeTool === 'memo-summary') {
    return (
      <ToolCard title={selected.label} desc="상담 후 남긴 메모를 요약, 고객 니즈, 확인 필요, 다음 액션, 후속 메시지 초안으로 정리합니다.">
        <textarea
          className="min-h-[180px] w-full rounded border border-slate-300 px-3 py-2 text-sm"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="상담 메모를 붙여넣으세요. 예: 암 진단비 보완 희망, 보험료 15만원 이내, 다음 주 기존 증권 전달 예정"
        />
        {memoSummary ? (
          <>
            <ResultBox lines={memoSummary} />
            <div className="flex justify-end">
              <Button type="button" variant="sgezOutline" size="sm" onClick={() => copyLines(memoSummary)}>요약 복사</Button>
            </div>
          </>
        ) : (
          <ResultBox lines={['상담 내용을 입력하면 요약 결과가 표시됩니다.', '민감한 진단명·주민번호 등은 입력하지 않는 방향을 권장합니다.']} />
        )}
      </ToolCard>
    )
  }
  return (
    <ToolCard title={selected.label} desc={selected.desc}>
      <ul className="space-y-2 text-sm text-slate-700">
        {(presets[activeTool] || ['이 도구는 다음 단계에서 AI 생성 기능과 연결됩니다.']).map((line) => (
          <li key={line} className="rounded border border-slate-200 bg-slate-50 px-3 py-2">{line}</li>
        ))}
      </ul>
    </ToolCard>
  )
}

function ToolCard({ title, desc, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      <p className="mt-1 text-sm leading-6 text-slate-600">{desc}</p>
      <div className="mt-4 space-y-3">{children}</div>
    </section>
  )
}

function ResultBox({ lines }) {
  return (
    <div className="rounded border border-sgez-primary/20 bg-sgez-background px-3 py-3 text-sm leading-6 text-slate-800">
      {lines.map((line) => <div key={line}>{line}</div>)}
    </div>
  )
}

function SilsonHistoryTool({ onOpenCalculator }) {
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">실비의료비 변천사</h2>
            {/* <p className="mt-1 text-sm leading-6 text-slate-600">
              최초 계약일·갱신 이력으로 세대를 나누고, 급여/비급여·입원/통원 구조가 어떻게 달라졌는지 한눈에 정리합니다. 실제 보장은 증권 약관·보험사 심사 기준을 따릅니다.
            </p> */}
          </div>
          <Button type="button" variant="sgezOutline" size="sm" className="shrink-0" onClick={onOpenCalculator}>
            실비 예상 계산기
          </Button>
        </div>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3">
          <div className="text-sm font-semibold text-slate-800">시기·세대 한눈에 보기</div>
        </div>
        <div className="divide-y divide-slate-100 sm:hidden">
          {GENERATIONS.map((item) => {
            const detail = GENERATION_DETAILS[item.value]
            return (
              <div key={item.value} className="px-4 py-3">
                <div className="text-sm font-bold text-slate-900">{item.label}</div>
                <div className="mt-0.5 text-xs text-slate-500">{detail.period}</div>
                <div className="mt-2 text-xs leading-5 text-slate-600">
                  <span className="mr-1.5 inline-block rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-600">{detail.badge}</span>
                  {detail.title}
                </div>
              </div>
            )
          })}
        </div>
        <div className="hidden overflow-x-auto px-2 py-3 sm:block sm:px-4">
          <table className="w-full min-w-[640px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-sgez-surface-sunken text-xs font-semibold text-slate-500">
                <th className="px-3 py-2">세대</th>
                <th className="px-3 py-2">대략적 시기</th>
                <th className="px-3 py-2">구조적 특징</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {GENERATIONS.map((item) => {
                const detail = GENERATION_DETAILS[item.value]
                return (
                  <tr key={item.value} className="align-top">
                    <td className="px-3 py-2 font-semibold text-slate-900">{item.label}</td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{detail.period}</td>
                    <td className="px-3 py-2 text-slate-600">
                      <span className="mr-2 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{detail.badge}</span>
                      {detail.title}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3">
          <div className="text-sm font-semibold text-slate-800">세대별 요약</div>
        </div>
        <div className="grid gap-3 px-4 py-4 md:grid-cols-2">
          {GENERATIONS.map((item) => {
            const detail = GENERATION_DETAILS[item.value]
            return (
              <div key={item.value} className="rounded-lg border border-slate-200 bg-slate-50/80 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-bold text-slate-900">{item.label}</div>
                  <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200">{detail.badge}</span>
                </div>
                <div className="mt-1 text-xs font-medium text-slate-500">{detail.period}</div>
                <div className="mt-2 text-sm font-semibold text-slate-800">{detail.title}</div>
                <ul className="mt-2 space-y-1 text-xs leading-5 text-slate-600">
                  {detail.points.map((point) => <li key={point}>- {point}</li>)}
                </ul>
              </div>
            )
          })}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-800">세대별 핵심 비교</div>
        <div className="divide-y divide-slate-100 sm:hidden">
          {GENERATION_COMPARISON_ROWS.map((row) => (
            <div key={row.label} className="space-y-2 px-4 py-3">
              <div className="text-xs font-bold text-slate-900">{row.label}</div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px] leading-snug text-slate-600">
                <div><span className="font-semibold text-slate-500">1세대 </span>{row.gen1}</div>
                <div><span className="font-semibold text-slate-500">2세대 </span>{row.gen2}</div>
                <div><span className="font-semibold text-slate-500">3세대 </span>{row.gen3}</div>
                <div><span className="font-semibold text-slate-500">4세대 </span>{row.gen4}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="hidden overflow-x-auto sm:block">
          <table className="w-full min-w-[680px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-sgez-surface-sunken text-xs font-semibold text-slate-500">
                <th className="px-3 py-2">구분</th>
                <th className="px-3 py-2">1세대</th>
                <th className="px-3 py-2">2세대</th>
                <th className="px-3 py-2">3세대</th>
                <th className="px-3 py-2">4세대</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {GENERATION_COMPARISON_ROWS.map((row) => (
                <tr key={row.label} className="align-top">
                  <td className="px-3 py-2 font-semibold text-slate-800">{row.label}</td>
                  <td className="px-3 py-2 text-slate-600">{row.gen1}</td>
                  <td className="px-3 py-2 text-slate-600">{row.gen2}</td>
                  <td className="px-3 py-2 text-slate-600">{row.gen3}</td>
                  <td className="px-3 py-2 text-slate-600">{row.gen4}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* <div className="border-t border-slate-100 px-4 py-3 text-xs leading-5 text-slate-500">
          1~2세대 실비는 현재 판매 상품과 비교해 유리한 경우가 있어, 해지·전환 전에는 증권·보장 비교가 필요합니다.
        </div> */}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3">
          <div className="text-sm font-semibold text-slate-800">이 서비스 예상 계산기와 맞춘 가정</div>
        </div>
        <div className="divide-y divide-slate-100 sm:hidden">
          {CALCULATOR_LOGIC_ROWS.map((row) => (
            <div key={row.label} className="space-y-2 px-4 py-3">
              <div className="text-xs font-bold text-slate-900">{row.label}</div>
              <div className="grid grid-cols-1 gap-1.5 text-[11px] leading-snug text-slate-600">
                <div><span className="font-semibold text-slate-500">1세대 </span>{row.gen1}</div>
                <div><span className="font-semibold text-slate-500">2세대 </span>{row.gen2}</div>
                <div><span className="font-semibold text-slate-500">3세대 </span>{row.gen3}</div>
                <div><span className="font-semibold text-slate-500">4세대 </span>{row.gen4}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="hidden overflow-x-auto px-2 py-3 sm:block sm:px-4">
          <table className="w-full min-w-[720px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-sgez-surface-sunken text-xs font-semibold text-slate-500">
                <th className="px-3 py-2">항목</th>
                <th className="px-3 py-2">1세대</th>
                <th className="px-3 py-2">2세대</th>
                <th className="px-3 py-2">3세대</th>
                <th className="px-3 py-2">4세대</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {CALCULATOR_LOGIC_ROWS.map((row) => (
                <tr key={row.label} className="align-top">
                  <td className="px-3 py-2 font-semibold text-slate-800">{row.label}</td>
                  <td className="px-3 py-2 text-xs leading-5 text-slate-600">{row.gen1}</td>
                  <td className="px-3 py-2 text-xs leading-5 text-slate-600">{row.gen2}</td>
                  <td className="px-3 py-2 text-xs leading-5 text-slate-600">{row.gen3}</td>
                  <td className="px-3 py-2 text-xs leading-5 text-slate-600">{row.gen4}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-800">상담 체크포인트</div>
        <div className="grid gap-2 px-3 py-3 sm:gap-3 sm:px-4 sm:py-4 md:grid-cols-2">
          {HISTORY_CONSULT_CHECKS.map((block) => (
            <div key={block.title} className="rounded-lg border border-slate-200 bg-slate-50/80 px-2.5 py-2 sm:rounded-xl sm:p-3">
              <div className="text-[11px] font-bold text-slate-900 sm:text-xs">{block.title}</div>
              <ul className="mt-1 space-y-0.5 text-[11px] leading-snug text-slate-600 sm:mt-2 sm:space-y-1 sm:text-xs sm:leading-5">
                {block.items.map((line) => <li key={line}>- {line}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-4 shadow-sm">
        <div className="text-sm font-semibold text-amber-950">자주 생기는 오해</div>
        <ul className="mt-2 space-y-1 text-xs leading-5 text-amber-950/90">
          {HISTORY_PITFALLS.map((line) => <li key={line}>- {line}</li>)}
        </ul>
      </section>
    </div>
  )
}

export default function MedicalExpenseReimbursementCalculator() {
  const { show } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  /** URL(`?tool=`)만 기준으로 한다. 로컬 state와 동기화 이펙트를 쓰면 클릭 직후 stale URL 때문에 탭이 한 번 되돌아가는 버그가 난다. */
  const activeTool = useMemo(() => {
    const id = searchParams.get('tool')
    return SUPPORT_TOOLS.some((tool) => tool.id === id) ? id : 'medical'
  }, [searchParams])

  const [form, setForm] = useState(DEFAULT_FORM)
  const [uploading, setUploading] = useState(false)
  const [ocrResult, setOcrResult] = useState(null)
  const result = useMemo(() => calculateEstimate(form), [form])

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }))
  const selectTool = (toolId) => {
    if (toolId === 'medical') {
      setSearchParams({}, { replace: true })
    } else {
      setSearchParams({ tool: toolId })
    }
  }

  const applyOcrFields = (fields) => {
    if (!fields) return
    setForm(prev => ({
      ...prev,
      benefitPatientPaid: String(fields.benefitPatientPaid?.value || prev.benefitPatientPaid || ''),
      nonBenefitAmount: String(fields.nonBenefitAmount?.value || prev.nonBenefitAmount || ''),
      pharmacyBenefitPatientPaid: String(fields.pharmacyBenefitPatientPaid?.value || prev.pharmacyBenefitPatientPaid || ''),
      pharmacyNonBenefitAmount: String(fields.pharmacyNonBenefitAmount?.value || prev.pharmacyNonBenefitAmount || ''),
    }))
  }

  const uploadReceipt = async (file) => {
    if (!file) return
    setUploading(true)
    setOcrResult(null)
    try {
      const body = new FormData()
      body.append('file', file)
      const res = await api.post('/customer-support-tools/medical-expense-reimbursement/receipt-extract', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const data = res?.data?.data || res?.data
      setOcrResult(data)
      applyOcrFields(data?.fields)
      show('영수증에서 금액을 추출했습니다. 계산 전 금액을 확인해주세요.', 'success')
    } catch (e) {
      const msg = e?.response?.data?.message || '영수증 OCR 처리에 실패했습니다.'
      show(msg, 'error')
    } finally {
      setUploading(false)
    }
  }

  return (
    <PageFrame>
      <PageHeader
        title="고객지원도구"
        titleAccessory={<MenuGuideButton slug="customer-tools" title="고객지원도구 가이드" />}
      />
      {/* <p className="mt-1 text-sm text-slate-600">
        상담 중 바로 쓰는 계산기와 독립 확인 도구를 왼쪽 목록에서 골라 사용합니다. 고객별 고지 확인, 상담 질문, 상담 후 요약은 고객상세의 상담 탭에서 진행합니다.
      </p> */}

      <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-[minmax(150px,10%)_minmax(0,90%)]">
        <aside className="min-w-0 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm lg:sticky lg:top-24 lg:self-start">
          <div className="mb-2 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">도구 목록</div>
          <div className="flex gap-1 overflow-x-auto lg:block lg:space-y-1 lg:overflow-visible">
            {SUPPORT_TOOLS.map((tool) => (
              <button
                key={tool.id}
                type="button"
                className={`min-w-[128px] rounded-lg px-2.5 py-2 text-left transition lg:w-full ${
                  activeTool === tool.id ? 'border border-sgez-primary bg-sky-50 text-sgez-primary shadow-sm ring-1 ring-sgez-primary/10' : 'text-slate-700 hover:bg-slate-50'
                }`}
                onClick={() => selectTool(tool.id)}
              >
                <div className="text-xs font-bold">{tool.label}</div>
                <div className={`mt-0.5 hidden text-[10px] leading-4 lg:block ${activeTool === tool.id ? 'text-sgez-primary/75' : 'text-slate-500'}`}>{tool.desc}</div>
              </button>
            ))}
          </div>
        </aside>

        <main className="min-w-0">
          {activeTool === 'medical' ? (
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(300px,0.55fr)]">
            <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">실비 예상 계산기</h2>
                {/* <p className="mt-1 text-sm leading-6 text-slate-600">
                  진료비 영수증을 업로드하거나 금액을 직접 입력해 실손보험 예상 환급액을 계산합니다. 세대 구분·변천사는 왼쪽 목록의 <strong className="font-semibold text-slate-800">실비의료비 변천사</strong>에서 확인하세요.
                </p>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  실제 지급액은 보험사 심사와 약관에 따라 달라질 수 있습니다.
                </p> */}
              </div>
              <Button type="button" variant="sgezOutline" size="sm" className="shrink-0" onClick={() => selectTool('silson-history')}>
                변천사 보기
              </Button>
            </div>
          </div>

          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">영수증 업로드</h3>
                <p className="mt-1 text-sm text-slate-500">PDF, JPG, PNG 파일을 지원합니다.</p>
              </div>
              <label className="inline-flex h-8 cursor-pointer items-center justify-center rounded-md bg-sgez-primary px-3 text-xs font-medium text-white shadow-sgez-soft hover:bg-sgez-primary-dark">
                {uploading ? '분석 중...' : '영수증 선택'}
                <input
                  type="file"
                  accept="application/pdf,image/png,image/jpeg"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => uploadReceipt(e.target.files?.[0])}
                />
              </label>
            </div>
            {ocrResult && (
              <div className="px-4 py-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-bold text-slate-900">OCR 추출 결과</div>
                    <div className="mt-1 text-xs text-slate-500">{ocrResult.originalName || '영수증'} · {ocrResult.textSource}</div>
                  </div>
                  <Button
                    type="button"
                    variant="sgezOutline"
                    size="sm"
                    onClick={() => applyOcrFields(ocrResult.fields)}
                  >
                    다시 반영
                  </Button>
                </div>
                {Array.isArray(ocrResult.warnings) && ocrResult.warnings.length > 0 && (
                  <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
                    {ocrResult.warnings.join(' ')}
                  </div>
                )}
                <div className="mt-3 overflow-x-auto rounded border border-slate-200 bg-white">
                  <table className="w-full min-w-[560px] border-collapse text-left text-xs">
                    <thead>
                      <tr className="border-b border-slate-100 bg-sgez-surface-sunken text-slate-500">
                        <th className="px-2 py-2">항목</th>
                        <th className="px-2 py-2">추출 금액</th>
                        <th className="px-2 py-2">신뢰도</th>
                        <th className="px-2 py-2">인식 문장</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {ocrFieldRows(ocrResult.fields).map((row) => (
                        <tr key={row.key}>
                          <td className="px-2 py-2 font-semibold text-slate-700">{row.label}</td>
                          <td className="px-2 py-2 tabular-nums text-slate-900">{row.value ? formatWon(row.value) : '-'}</td>
                          <td className="px-2 py-2 text-slate-600">{row.value ? confidenceLabel(row.confidence) : '-'}</td>
                          <td className="px-2 py-2 text-slate-500">{row.sourceLine || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-800">1. 실비 조건</div>
            <div className="grid gap-4 px-4 py-4 md:grid-cols-12">
              <label className="block md:col-span-6">
                <span className="text-sm font-semibold text-slate-800">실비 세대</span>
                <select className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm" value={form.generation} onChange={(e) => updateForm('generation', e.target.value)}>
                  {GENERATIONS.map(item => <option key={item.value} value={item.value}>{item.label} · {item.desc}</option>)}
                </select>
              </label>
              <label className="block md:col-span-2">
                <span className="text-sm font-semibold text-slate-800">입원/통원</span>
                <select className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm" value={form.visitType} onChange={(e) => updateForm('visitType', e.target.value)}>
                  <option value="OUTPATIENT">통원</option>
                  <option value="INPATIENT">입원</option>
                </select>
              </label>
              <label className="block md:col-span-4">
                <span className="text-sm font-semibold text-slate-800">병원 규모</span>
                <select className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm" value={form.hospitalType} onChange={(e) => updateForm('hospitalType', e.target.value)}>
                  {HOSPITAL_TYPES.map(item => <option key={item.value} value={item.value}>{item.label} · 공제 {formatWon(item.deductible)}</option>)}
                </select>
              </label>
              {form.generation === 'GEN4' && (
                <label className="block md:col-span-6">
                  <span className="text-sm font-semibold text-slate-800">비급여 특약</span>
                  <select className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm" value={form.hasGen4NonBenefitRider ? 'Y' : 'N'} onChange={(e) => updateForm('hasGen4NonBenefitRider', e.target.value === 'Y')}>
                    <option value="Y">가입</option>
                    <option value="N">미가입</option>
                  </select>
                </label>
              )}
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-800">2. 진료비 금액</div>
            <div className="grid gap-4 px-4 py-4 md:grid-cols-2">
              <AmountInput label="급여 본인부담금" value={form.benefitPatientPaid} onChange={(v) => updateForm('benefitPatientPaid', v)} />
              <AmountInput label="비급여 금액" value={form.nonBenefitAmount} onChange={(v) => updateForm('nonBenefitAmount', v)} />
              <AmountInput label="약제비 급여 본인부담금" value={form.pharmacyBenefitPatientPaid} onChange={(v) => updateForm('pharmacyBenefitPatientPaid', v)} />
              <AmountInput label="약제비 비급여 금액" value={form.pharmacyNonBenefitAmount} onChange={(v) => updateForm('pharmacyNonBenefitAmount', v)} />
            </div>
          </section>
            </div>

        <aside className="xl:sticky xl:top-24 xl:self-start">
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-800">계산 결과</div>
            <div className="px-4 py-4">
              <div className="text-sm text-slate-500">예상 환급액</div>
              <div className="mt-1 text-3xl font-bold tracking-tight text-slate-900">{formatWon(result.estimated)}</div>
              <div className="mt-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
                계산 기준: {GENERATIONS.find((item) => item.value === form.generation)?.label || '-'} · {form.visitType === 'INPATIENT' ? '입원' : '통원'} · {(HOSPITAL_TYPES.find((item) => item.value === form.hospitalType) || HOSPITAL_TYPES[0]).label}
                {form.generation === 'GEN4' ? ` · 비급여 특약 ${form.hasGen4NonBenefitRider ? '가입' : '미가입'}` : ''}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs text-slate-500">입력 총액</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{formatWon(result.totalPaid)}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs text-slate-500">예상 자기부담</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{formatWon(result.selfPaid)}</div>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {result.items.map(item => (
                  <div key={`${item.label}-${item.note}`} className="rounded-lg border border-slate-200 bg-white p-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-slate-800">{item.label}</span>
                      <span className="text-sm font-bold text-slate-900">{formatWon(item.amount)}</span>
                    </div>
                    <div className="mt-1 text-xs leading-5 text-slate-500">{item.note}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded border border-amber-200 bg-amber-50 px-3 py-3 text-xs leading-5 text-amber-900">
                이 계산기는 표준적인 세대별 공제 구조를 단순화한 예상치입니다. 보험사별 약관, 가입 특약, 보상 한도, 면책 사항에 따라 실제 지급액이 달라질 수 있습니다.
              </div>
            </div>
          </div>
        </aside>
          </div>
          ) : activeTool === 'silson-history' ? (
            <SilsonHistoryTool onOpenCalculator={() => selectTool('medical')} />
          ) : (
            <SimpleSupportTool activeTool={activeTool} />
          )}
        </main>
      </div>
    </PageFrame>
  )
}
